import { useMemo } from 'react';
import { ImageIcon, Loader2 } from 'lucide-react';
import { NarrationSceneReorderList } from '@/components/NarrationSceneReorderList';
import { segmentHasImage } from '@/lib/scriptBasedPipeline';

/**
 * List / horizontal reel / vertical reel for script-based pipeline image progress.
 */
export function ScriptBasedPipelineSceneViews({
  segments = [],
  job = null,
  imagesInProgress = false,
  onRetryImage,
  imageRetryBusy = null,
  className = '',
}) {
  const scenes = useMemo(
    () => (segments || []).map((seg) => ({
      ...seg,
      scene_text: seg.visual || seg.image_prompt || seg.text || seg.authored_script,
    })),
    [segments],
  );

  const imaged = scenes.filter((s) => segmentHasImage(s)).length;
  const running = scenes.filter((s) => String(s.image_status || '').toLowerCase() === 'running').length;
  const total = scenes.length;

  if (!scenes.length) return null;

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden ${className}`.trim()}
      data-testid="script-based-pipeline-scene-views"
    >
      <div className="px-3 py-2 border-b border-slate-200 bg-white flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">Scene previews</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {imagesInProgress
              ? `${imaged}/${total} images ready${running ? ` · ${running} generating` : ''}`
              : `${total} scene${total === 1 ? '' : 's'} · switch list, horizontal reel, or vertical reel`}
          </p>
        </div>
        {imagesInProgress ? (
          <div className="flex items-center gap-1.5 text-[11px] text-violet-700">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Queued on workers
          </div>
        ) : imaged < total ? (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <ImageIcon className="w-3.5 h-3.5" />
            Images appear here as they finish
          </div>
        ) : null}
      </div>
      <div className="p-3 max-h-[min(420px,50vh)] overflow-y-auto bg-[#0a0a0a] rounded-b-lg">
        <NarrationSceneReorderList
          scenes={scenes}
          disabled
          showViewToggle
          storageKey="script-based-pipeline-scene-view"
          className="script-based-pipeline-scene-views__list"
          onRetryImage={onRetryImage}
          imageRetryBusy={imageRetryBusy}
        />
      </div>
      {job?.message && imagesInProgress ? (
        <p className="px-3 pb-2 text-[10px] text-slate-500 border-t border-slate-100 pt-2">
          {job.message}
        </p>
      ) : null}
    </div>
  );
}
